import { useMutation } from "@apollo/client";
import { useState } from "react";
import { GENERATE_DOWNLOAD_LINK } from "../../../graphql/mutations/fileManager";

/**
 * Custom hook for generating download links
 * @returns {Object} Hook return values
 */
export const useGenerateDownloadLink = () => {
  const [loadingFileId, setLoadingFileId] = useState(null);
  const [error, setError] = useState(null);

  const [generateDownloadLinkMutation] = useMutation(GENERATE_DOWNLOAD_LINK);

  const generateLink = async (fileId) => {
    setLoadingFileId(fileId);
    setError(null);

    try {
      const { data, errors } = await generateDownloadLinkMutation({
        variables: { fileRequestId: fileId },
      });

      if (errors) {
        throw new Error(errors[0]?.message || "Failed to generate download link");
      }

      const url = data?.generateDownloadLink?.url;

      if (!url) {
        throw new Error("No download URL received");
      }

      // Open URL in new tab
      window.open(url, "_blank", "noopener,noreferrer");

      setLoadingFileId(null);
      return { success: true, url };
    } catch (err) {
      console.error("Error generating download link:", err);

      let errorMessage = "Failed to generate download link";

      // Handle specific error codes
      if (
        err.message?.includes("410") ||
        err.message?.toLowerCase().includes("expired")
      ) {
        errorMessage = "File has expired. Please generate a new export.";
      } else if (
        err.message?.includes("403") ||
        err.message?.toLowerCase().includes("unauthorized")
      ) {
        errorMessage = "You don't have permission to download this file.";
      } else if (err.message?.includes("500")) {
        errorMessage = "Download failed. Please try again.";
      }

      setError(errorMessage);
      setLoadingFileId(null);

      return { success: false, error: errorMessage };
    }
  };

  return {
    generateLink,
    loading: loadingFileId !== null,
    loadingFileId,
    error,
    setError,
  };
};
